import * as React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { PERMISSION } from 'src/features/auth';
import { useListGatewayPasses } from 'src/features/gatewayPass/api/useListGatewayPasses';
import { GATEWAY_PASSES_PANEL, STAY_PERIODS_PANEL } from 'src/features/gatewayPass/utils/constants';
import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import BackButton from 'src/features/navigation/components/BackButton';
import { PATH } from 'src/features/navigation/utils/constants';
import { useGetActiveStay } from 'src/features/stay/api/useGetActiveStay';
import { useGetPastStay } from 'src/features/stay/api/useGetPastStay';
import { useGetPricingPlanChangeOptions } from 'src/features/stay/api/useGetPricingPlanChangeOptions';
import { useListActiveStayPeriods } from 'src/features/stay/api/useListActiveStayPeriods';
import { useListPastStayPeriods } from 'src/features/stay/api/useListPastStayPeriods';
import DashboardGeneralBlock from 'src/features/stay/components/dashboard/DashboardGeneralBlock';
import StayDashboardTables from 'src/features/stay/components/dashboard/StayDashboardTables';
import VisitorDataField from 'src/features/stay/components/dashboard/VisitorDataField';
import ChangeActiveStayPricingPlanFormModal
    from 'src/features/stay/components/forms/ChangeActiveStayPricingPlanFormModal';
import ConvertActiveStayToMembershipModal from 'src/features/stay/components/forms/ConvertActiveStayToMembershipModal';
import ExitFromParkingFormModal from 'src/features/stay/components/forms/ExitFromParkingFormModal';
import { ActiveStay, PastStay, PERIOD_VISITOR_CATEGORY, STAY_TYPE } from 'src/features/stay/types';
import { validationMessages } from 'src/features/validations/utils/messages';
import ImageCarousel from 'src/features/visitor/components/carousel/ImageCarousel';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { formatTimestampToCustomId } from 'src/features/visitor/utils/timestampToId';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useLiveStayDuration } from 'src/hooks/useLiveStayDuration';
import { useParking } from 'src/providers/ParkingProvider';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';
import { GetDurationFromMinutes } from 'src/utils/getDurationFromMinutes';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';

type Props = {
    stayType: STAY_TYPE;
};

export default function StayDashboard({ stayType }: Props) {
    const { formatMessage, locale, formatNumber } = useIntl();
    const { stayId = '' } = useParams<{ stayId: string | undefined }>();
    const settingsData = React.useContext(SettingsContext);

    const manageActiveStays = useHasPermissions([PERMISSION.MANAGE_ACTIVE_STAYS]);
    const manageMembership = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIPS]);

    const { sidePanelState } = useSidePanel();
    const isSidePanelOpen = React.useMemo<boolean>(
        () => sidePanelState[STAY_PERIODS_PANEL] || sidePanelState[GATEWAY_PASSES_PANEL],
        [sidePanelState],
    );

    const { parkingId } = useParking();
    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(visitorMessages.parkingStayDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: pricingPlanChangeOptions } = useGetPricingPlanChangeOptions({
        parkingId,
        stayId,
        config: {
            enabled: !!stayId && stayType === STAY_TYPE.ACTIVE,
        },
    });

    const { data: selectedActiveStay } = useGetActiveStay({
        parkingId,
        stayId,
        config: {
            enabled: !!stayId && stayType === STAY_TYPE.ACTIVE,
        },
    });

    const { data: selectedPastStay } = useGetPastStay({
        parkingId,
        stayId,
        config: {
            enabled: !!stayId && stayType === STAY_TYPE.PAST,
        },
    });

    const { data: activeStayPeriodsData } = useListActiveStayPeriods({
        parkingId,
        stayId,
        config: { enabled: !!stayId && stayType === STAY_TYPE.ACTIVE },
    });

    const { data: pastStayPeriodsData } = useListPastStayPeriods({
        parkingId,
        stayId,
        config: { enabled: !!stayId && stayType === STAY_TYPE.PAST },
    });

    const { data: filteredGatewayPassesByStayId } = useListGatewayPasses({
        parkingId,
        ...(stayType === STAY_TYPE.ACTIVE ? { active_stay: stayId } : {}),
        ...(stayType === STAY_TYPE.PAST ? { past_stay: stayId } : {}),
        ordering: 'time',
        limit: 500,
        config: { enabled: !!stayId },
    });

    const stayDataToUse = (stayType === STAY_TYPE.ACTIVE ? selectedActiveStay : selectedPastStay) as
        | ActiveStay
        | PastStay;

    const isActiveStayClient = Boolean(
        stayType === STAY_TYPE.ACTIVE && selectedActiveStay?.period_visitor_category === PERIOD_VISITOR_CATEGORY.CLIENT,
    );

    const zeroAmount = formatNumber(0, {
        style: 'currency',
        currency: stayDataToUse?.currency ?? settingsData.default_currency,
    });

    const totalRemainingAmount = stayDataToUse?.total_amounts?.total_remaining_amount
        ? formatNumber(stayDataToUse?.total_amounts?.total_remaining_amount / 100, {
              style: 'currency',
              currency: stayDataToUse?.currency ?? settingsData.default_currency,
          })
        : zeroAmount;

    const totalDueAmount =
        stayType === STAY_TYPE.PAST && (stayDataToUse as PastStay)?.total_due_amount
            ? formatNumber((stayDataToUse as PastStay)?.total_due_amount / 100, {
                  style: 'currency',
                  currency: stayDataToUse?.currency ?? settingsData.default_currency,
              })
            : zeroAmount;

    const paymentDue = stayType === STAY_TYPE.ACTIVE ? totalRemainingAmount : totalDueAmount;

    const gatewayPassesPhotos = React.useMemo(() => {
        return filteredGatewayPassesByStayId?.results.map((pass) => {
            return {
                image: pass.photo,
                title: pass.gateway.translations.find((z) => z.language === locale)?.name,
                subtitle: `${formatMessage(visitorMessages.pass)} ${formatTimestampToCustomId(pass.time)}`,
            };
        });
    }, [filteredGatewayPassesByStayId?.results, formatMessage, locale]);

    const activeStayPeriodsCount = activeStayPeriodsData?.results.length ?? 0;
    const pastStayPeriodsCount = pastStayPeriodsData?.results.length ?? 0;
    const stayPeriodsCount = stayType === STAY_TYPE.ACTIVE ? activeStayPeriodsCount : pastStayPeriodsCount;

    const formattedEntryTime = getFormattedDateTimeForDetailPanel(stayDataToUse?.entry_time);
    const formattedExitTime =
        stayDataToUse && 'exit_time' in stayDataToUse
            ? getFormattedDateTimeForDetailPanel(stayDataToUse?.exit_time)
            : '-';

    const durationToDisplay = useLiveStayDuration(stayDataToUse?.entry_time, stayDataToUse?.exit_time);

    const additionalIdentifiers = React.useMemo(() => {
        return stayDataToUse?.additional_identifiers.map((identifier) => `#${identifier.value}`).join(', ') || '-';
    }, [stayDataToUse]);

    const totalPaidAmount = stayDataToUse?.total_amounts?.total_paid_amount
        ? formatNumber(stayDataToUse?.total_amounts?.total_paid_amount / 100, {
              style: 'currency',
              currency: stayDataToUse?.currency ?? settingsData.default_currency,
          })
        : zeroAmount;

    const totalValidatedAmount = stayDataToUse?.total_amounts?.total_validated_amount
        ? formatNumber(stayDataToUse?.total_amounts?.total_validated_amount / 100, {
              style: 'currency',
              currency: stayDataToUse?.currency ?? settingsData.default_currency,
          })
        : zeroAmount;

    const validatedTime = GetDurationFromMinutes(stayDataToUse?.total_amounts?.total_validated_time ?? 0);
    const showChangePricingPlanBtn = pricingPlanChangeOptions?.can_be_changed && manageActiveStays;

    const dashboardData = {
        section1: [
            {
                title: visitorMessages.visitor,
                value: <VisitorDataField stayData={stayDataToUse} stayType={stayType} />,
            },
            { title: visitorMessages.enteredAt, value: formattedEntryTime ?? '-' },
            { title: visitorMessages.exit, value: formattedExitTime },
            { title: visitorMessages.paymentDue, value: paymentDue ?? '-' },
            { title: visitorMessages.paidAmount, value: totalPaidAmount },
            { title: validationMessages.validatedAmount, value: totalValidatedAmount ?? '-' },
            {
                title: validationMessages.validatedTime,
                value: validatedTime ?? '-',
            },
            { title: visitorMessages.additionalIdentifiers, value: additionalIdentifiers },
            { title: visitorMessages.duration, value: durationToDisplay },
            {
                title: visitorMessages.periods,
                value: `${stayPeriodsCount} ${formatMessage(visitorMessages.periodsPlural, { count: stayPeriodsCount })}`,
            },
            {
                title: visitorMessages.manuallyCreated,
                value: <BooleanBadge booleanField={stayDataToUse?.is_manually_created} />,
            },
        ],
    };
    if (!stayId) return null;

    return (
        <>
            <BackButton customRoute={`/${PATH.VISITORS}/visitors`} />
            <div className="flex flex-col gap-4 sm:gap-6 md:flex-row">
                <SectionBlock>
                    <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                        <SectionBlockHead>
                            <SectionBlockTitle>
                                <div>{stayDataToUse?.main_identifier.value}</div>
                            </SectionBlockTitle>
                            <SectionBlockSubtitle>{stayDataToUse && `#${stayDataToUse?.ref}`}</SectionBlockSubtitle>
                        </SectionBlockHead>
                        {isActiveStayClient && manageMembership && (
                            <ConvertActiveStayToMembershipModal selectedActiveStay={selectedActiveStay}>
                                <Button className="cursor-pointer">
                                    {formatMessage(visitorMessages.convertActiveStay)}
                                </Button>
                            </ConvertActiveStayToMembershipModal>
                        )}
                        {showChangePricingPlanBtn && (
                            <ChangeActiveStayPricingPlanFormModal
                                stayId={stayId}
                                pricingPlanChangeOptions={pricingPlanChangeOptions}
                            >
                                <Button className="cursor-pointer" variant="outline">
                                    {formatMessage(visitorMessages.changePricingPlan)}
                                </Button>
                            </ChangeActiveStayPricingPlanFormModal>
                        )}
                        {stayType === STAY_TYPE.ACTIVE && selectedActiveStay && manageActiveStays && (
                            <ExitFromParkingFormModal activeStay={selectedActiveStay}>
                                <Button className="cursor-pointer" variant="destructiveLight">
                                    {formatMessage(visitorMessages.exitFromParking)}
                                </Button>
                            </ExitFromParkingFormModal>
                        )}
                    </SectionBlockHeader>
                    <div className="flex w-full flex-col gap-2 lg:flex-row lg:items-stretch">
                        <DashboardGeneralBlock
                            dashboardData={dashboardData}
                            indexForLastSeparator={0}
                            className="w-full flex-1 2xl:flex-2"
                        />
                        {/*For some reason if we don't fixate its width first it doesn't apply the flex-1*/}
                        {!isSidePanelOpen && (
                            <ImageCarousel imageSlides={gatewayPassesPhotos} className="w-full max-w-98" />
                        )}
                    </div>
                </SectionBlock>
            </div>

            <StayDashboardTables stayType={stayType} stayId={stayDataToUse?.id} />
        </>
    );
}
